/**
 * MapLayers — presentational Leaflet layer sub-components extracted from
 * MapContainer so the container stays a thin orchestrator. Each layer owns its
 * own `onEachFeature` wiring; complex handlers are split into small helpers to
 * keep cyclomatic complexity low.
 */
import React, { useEffect } from "react";
import type { Map as LeafletMap } from "leaflet";
import {
  Marker,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";
import { FmMmlBasemapTiles } from "@/shared/ui/FmStandBasemapTiles";
import { BASEMAP_NATIVE_MAX_ZOOM } from "@/shared/config/basemapCoverage";
import { PropertyBoundaryLayer } from "@/components/Map/ForestContextLayers";
import RadarPlaybackLayer from "@/components/Map/RadarPlaybackLayer";
import DatedRasterLayer from "@/components/Map/DatedRasterLayer";
import RiskPinLayer from "@/components/Map/RiskPinLayer";
import type { PropertySearchResult } from "@/types/property";
import type { FmMapBasemapHint, FmMapPresentation } from "@/shared/config/mapPresentation";
import type { PropertyRiskPin } from "@/api/propertyHazardReport";
import {
  escapePopupText,
  interpolatePopupCopy,
  isPopupValueAvailable,
  popupNumericValue,
  popupPlainTextValue,
  popupRow,
  popupTextValue,
  type MapPopupCopy,
} from "@/components/Map/mapPopupHtml";

export type MapBaseMapId = "streets" | "satellite" | "terrain";

export interface MapBaseLayerProps {
  baseMapId: MapBaseMapId;
  /** The slot's `presentation.basemap` hints; the workspace reads only its tone. */
  basemap?: FmMapBasemapHint;
  attributionOpenTopoMap?: string;
  attributionCcBySa?: string;
}

const terrainAttribution = (
  mapLabel: string | undefined,
  licenseLabel: string | undefined,
) =>
  `&copy; <a href="https://opentopomap.org">${mapLabel ?? "OpenTopoMap"}</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">${licenseLabel ?? "CC-BY-SA"}</a>)`;

export const MapBaseLayer: React.FC<MapBaseLayerProps> = ({
  baseMapId,
  basemap,
  attributionOpenTopoMap,
  attributionCcBySa,
}) => {
  const tone = basemap?.tone ?? "standard";
  if (baseMapId === "satellite") {
    // MML Ortokuva via the api-key-injecting proxy — NOT Esri World_Imagery
    // (license grey zone, unknown capture dates in Finland).
    return <FmMmlBasemapTiles layer="ortokuva" tone={tone} />;
  }
  if (baseMapId === "terrain") {
    return (
      <TileLayer
        attribution={terrainAttribution(
          attributionOpenTopoMap,
          attributionCcBySa,
        )}
        url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
        maxZoom={17}
        maxNativeZoom={BASEMAP_NATIVE_MAX_ZOOM.opentopomap}
        className="fm-basemap-tiles"
      />
    );
  }
  // MML Maastokartta with built-in OSM fallback; direct OSM primary tiles
  // violate the OSMF tile usage policy at production volume.
  return <FmMmlBasemapTiles layer="maastokartta" tone={tone} />;
};

type LeafletInteractionHandler = {
  disable?: () => void;
  enable?: () => void;
  enabled?: () => boolean;
};

type LeafletMapWithOptionalHandlers = LeafletMap & {
  touchZoom?: LeafletInteractionHandler;
  boxZoom?: LeafletInteractionHandler;
  keyboard?: LeafletInteractionHandler;
  tap?: LeafletInteractionHandler;
};

const STATIC_INTERACTION_HANDLERS = [
  "scrollWheelZoom",
  "doubleClickZoom",
  "dragging",
  "touchZoom",
  "boxZoom",
  "keyboard",
  "tap",
] as const;

/** Disables pan/zoom on a snapshot map while preserving page scrolling. */
export const StaticMapInteractionController: React.FC = () => {
  const map = useMap() as LeafletMapWithOptionalHandlers;

  useEffect(() => {
    const container = map.getContainer();
    const previousTouchAction = container.style.touchAction;
    const keepWheelWithPage = (event: WheelEvent) => {
      event.stopImmediatePropagation();
    };
    const handlerStates = STATIC_INTERACTION_HANDLERS.map((handlerName) => {
      const handler = map[handlerName];
      const wasEnabled =
        typeof handler?.enabled === "function" ? handler.enabled() : undefined;
      handler?.disable?.();
      return { handler, wasEnabled };
    });

    container.style.touchAction = "pan-y";
    container.addEventListener("wheel", keepWheelWithPage, { capture: true });

    return () => {
      container.removeEventListener("wheel", keepWheelWithPage, {
        capture: true,
      });
      container.style.touchAction = previousTouchAction;
      handlerStates.forEach(({ handler, wasEnabled }) => {
        if (wasEnabled) handler?.enable?.();
      });
    };
  }, [map]);

  return null;
};

// Property boundary layer

interface PropertyBoundaryPopupLayerProps {
  data: GeoJSON.GeoJsonObject;
  layerKey: string;
  popupCopy: MapPopupCopy;
}

export const PropertyBoundaryPopupLayer = ({
  data,
  layerKey,
  popupCopy,
}: PropertyBoundaryPopupLayerProps) => (
  <PropertyBoundaryLayer
    layerKey={layerKey}
    data={data}
    onEachFeature={(feature, layer) => {
      const props = feature.properties ?? {};
      layer.bindPopup(
        `
        <section class="property-popup" aria-label="${escapePopupText(popupCopy.propertyAriaLabel)}">
          <h4 class="property-popup__title">${escapePopupText(popupCopy.propertyTitle)}</h4>
          <div class="property-popup__rows">
            ${popupRow(
              popupCopy.propertyArea,
              popupNumericValue(
                props.area_hectares,
                (value) =>
                  interpolatePopupCopy(popupCopy.propertyAreaValue, {
                    value: value.toFixed(2),
                  }),
                popupCopy,
              ),
            )}
            ${popupRow(popupCopy.municipality, popupTextValue(props.municipality, popupCopy))}
            ${popupRow(popupCopy.propertyType, popupTextValue(props.property_type, popupCopy))}
          </div>
        </section>
      `,
        { className: "property-popup-leaflet", maxWidth: 320 },
      );
    }}
  />
);

// Search markers layer

interface SearchMarkersLayerProps {
  markers: PropertySearchResult[];
  popupCopy: MapPopupCopy;
  onPropertySelected: (property: PropertySearchResult) => void;
}

export const SearchMarkersLayer = ({
  markers,
  popupCopy,
  onPropertySelected,
}: SearchMarkersLayerProps) => (
  <>
    {markers.map((property, index) => {
      // NO-FALLBACK: skip markers without an authoritative WGS84 center
      // rather than fabricating a 0,0 position.
      if (!property.coordinates) return null;
      const searchLocation = [property.municipality, property.region]
        .filter(isPopupValueAvailable)
        .join(", ");

      return (
        <Marker
          key={property.property_id ?? index}
          position={[property.coordinates.lat, property.coordinates.lng]}
        >
          <Popup className="map-search-popup-leaflet">
            <section
              className="map-search-popup"
              aria-label={popupCopy.searchAriaLabel}
            >
              <h4 className="map-search-popup__title">
                {popupPlainTextValue(property.address, popupCopy)}
              </h4>
              <p className="map-search-popup__location">
                {searchLocation || popupCopy.unavailable}
              </p>
              <button
                type="button"
                className="map-search-popup__action"
                onClick={() => onPropertySelected(property)}
              >
                {popupCopy.selectProperty}
              </button>
            </section>
          </Popup>
        </Marker>
      );
    })}
  </>
);

// Main-map surface overlays
// The non-stand react-leaflet children of the main map (basemap switcher, rain
// radar, property boundary, search markers), grouped so MapContainer can pass
// them through FmStandMap's mapChildren slot without hand-rolling a map body.

interface MapSurfaceOverlaysProps {
  baseMapId: MapBaseMapId;
  activePropertyId: string | null;
  propertyBbox: [number, number, number, number] | null;
  propertyBoundary: GeoJSON.GeoJsonObject | null;
  boundaryLayerKey: string;
  popupCopy: MapPopupCopy;
  searchMarkers: PropertySearchResult[];
  onPropertySelected: (property: PropertySearchResult) => void;
  /** Entitled live Forest Sentry risk pins; empty when Sentry is inactive. */
  riskPins: PropertyRiskPin[];
  selectedStandId?: string;
  onRiskPinClick: (standId: string, pin: PropertyRiskPin) => void;
  radarPlaybackEnabled: boolean;
  datedRasterOverlay?: import("@/shared/config/mapSlotContracts").FmDatedRasterOverlay | null;
  presentation?: FmMapPresentation;
}

export const MapSurfaceOverlays = ({
  baseMapId,
  presentation,
  activePropertyId,
  propertyBbox,
  propertyBoundary,
  boundaryLayerKey,
  popupCopy,
  searchMarkers,
  onPropertySelected,
  riskPins,
  selectedStandId,
  onRiskPinClick,
  radarPlaybackEnabled,
  datedRasterOverlay,
}: MapSurfaceOverlaysProps) => (
  <>
    <MapBaseLayer baseMapId={baseMapId} basemap={presentation?.basemap} />

    {radarPlaybackEnabled ? <RadarPlaybackLayer propertyBbox={propertyBbox} /> : null}
    <DatedRasterLayer overlay={datedRasterOverlay} />

    {/* Property boundary — render after stands so it reads on top. */}
    {propertyBoundary && (
      <PropertyBoundaryPopupLayer
        layerKey={boundaryLayerKey}
        data={propertyBoundary}
        popupCopy={popupCopy}
      />
    )}

    <SearchMarkersLayer
      markers={searchMarkers}
      popupCopy={popupCopy}
      onPropertySelected={onPropertySelected}
    />

    {/* Forest Sentry risk pins — render on top so they read above stands. */}
    {activePropertyId && riskPins.length > 0 && (
      <RiskPinLayer
        pins={riskPins}
        propertyId={activePropertyId}
        selectedStandId={selectedStandId}
        onPinClick={onRiskPinClick}
      />
    )}
  </>
);
